import { Maatregel, RijdtMaatregel, VsmStatus } from "@/entities/vsm";
import { createContext, Dispatch, SetStateAction, useCallback, useContext, useMemo, useState } from "react";

export type VsmEditorContextType = {
    /* The list of maatregelen being edited. Mutated by the CRUD actions below. */
    maatregelen: Maatregel[];
    /* The maatregel currently being edited (or a draft), or null when nothing is being edited. */
    editingMaatregel: Maatregel | null;
    setEditingMaatregel: (maatregel: Maatregel | null) => void;
    addMaatregel: (maatregel: Maatregel) => void;
    updateMaatregel: (maatregel: Maatregel) => void;
    deleteMaatregel: (maatregelId: string) => void;
    /* Accept the Spoorweb toelichting suggestion of a maatregel. */
    acceptToelichting: (maatregelId: string) => void;
    /* Remove the toelichting (or unaccepted Spoorweb suggestion) of a maatregel. */
    removeToelichting: (maatregelId: string) => void;
    /* Add a maatregel from another source (e.g. the old version) unless it is already present. */
    copyToNieuweVersie: (maatregel: Maatregel) => void;
    /* Whether a maatregel from another source has already been copied into this version. */
    isCopiedToNieuweVersie: (maatregel: Maatregel) => boolean;
    /* Reset the editor back to its initial maatregelen and clear any edit/draft state. */
    reset: () => void;
    // Which Rijdt-maatregelen are merged into the RijdtGroep, if the default is overridden.
    includeInRijdtGroep?: (maatregel: Maatregel) => maatregel is RijdtMaatregel;
    // global VSM properties
    toelichting: string;
    setToelichting: Dispatch<SetStateAction<string>>;
    resetToelichting: () => void;
    status: VsmStatus;
    setStatus: Dispatch<SetStateAction<VsmStatus>>;
};

export const VsmEditorContext = createContext<VsmEditorContextType | null>(null);

export const useVsmEditorContext = (): VsmEditorContextType => {
    const context = useContext(VsmEditorContext);
    if (!context) {
        throw new Error("useVsmEditorContext must be used within a VsmEditorProvider");
    }
    return context;
};

export const useVsmEditorMaatregelen = (): Maatregel[] => {
    return useVsmEditorContext().maatregelen;
};

export function useIsEditingMaatregel(): boolean {
    const context = useContext(VsmEditorContext);
    return context ? context.editingMaatregel !== null : false;
}

type MaatregelenEditorState = {
    /* Start creating a new maatregel with a temporary draft */
    startDraft: () => void;
    /* Start editing a maatregel */
    startEdit: (maatregel: Maatregel | null) => void;
    /* Save the maatregel, update existing or create new one */
    save: (maatregel: Maatregel) => void;
    /* Cancel creating or editing maatregel */
    cancel: (maatregel: Maatregel | null) => void;
    /* Copy existing (oude) maatregel to nieuwe versie */
    copyToNieuweVersie: (maatregel: Maatregel) => void;
    /* Whether an existing (oude) maatregel was already copied to the nieuwe versie */
    isCopiedToNieuweVersie: (maatregel: Maatregel) => boolean;
    /* Delete maatregel */
    deleteMaatregel: (maatregelId: string) => void;
    /* Accept the Spoorweb toelichting suggestion of a maatregel */
    acceptToelichting: (maatregelId: string) => void;
    /* Remove the toelichting (or unaccepted Spoorweb suggestion) of a maatregel */
    removeToelichting: (maatregelId: string) => void;

    /* The list of maatregelen applicable to the section type. This is mutated with CRUD actions */
    maatregelen: Maatregel[];
    /* The draft maatregel (after startDraft is called) that we can edit */
    draftMaatregel: Maatregel | null;
    /* Whether we can edit or create a new maatregel */
    isEditEnabled: boolean;
    /* Whether the current section is empty, also taking into account draft maatregel */
    isSectionEmpty: boolean;
};

export function useMaatregelenEditor(type: "Rijdt" | "RijdtNiet" | "Aanvullend"): MaatregelenEditorState {
    const context = useContext(VsmEditorContext);
    if (!context) {
        throw new Error("useMaatregelenEditor must be used within a VsmEditorProvider");
    }

    const [draftMaatregel, setDraftMaatregel] = useState<Maatregel | null>(null);
    const {
        maatregelen,
        addMaatregel,
        updateMaatregel,
        deleteMaatregel,
        copyToNieuweVersie,
        isCopiedToNieuweVersie,
        editingMaatregel,
        setEditingMaatregel,
        acceptToelichting,
        removeToelichting,
        includeInRijdtGroep,
    } = context;

    const filteredMaatregelen = useMemo(() => {
        return maatregelen.filter(Maatregel.filterBy(type, includeInRijdtGroep)).sort(Maatregel.sortByTreinserieAndType);
    }, [includeInRijdtGroep, maatregelen, type]);

    const startDraft = useCallback(() => {
        const draft = Maatregel.createEmpty(type);
        setEditingMaatregel(draft);
        setDraftMaatregel(draft);
    }, [setEditingMaatregel, type]);

    const onSaveMaatregel = useCallback(
        (nieuweMaatregel: Maatregel) => {
            if (nieuweMaatregel.status === "Nieuw") {
                // it was created new
                addMaatregel(nieuweMaatregel);
            } else {
                // update existing
                updateMaatregel(nieuweMaatregel);
            }
            setEditingMaatregel(null);
            setDraftMaatregel(null);
        },
        [addMaatregel, setEditingMaatregel, updateMaatregel]
    );

    const cancelCreateOrEditMaatregel = useCallback(
        (maatregel: Maatregel | null) => {
            if (maatregel?.status === "In Bewerking") {
                updateMaatregel(Maatregel.revert(maatregel));
            }
            setEditingMaatregel(null);
            setDraftMaatregel(null);
        },
        [setEditingMaatregel, updateMaatregel]
    );

    return useMemo(
        (): MaatregelenEditorState => ({
            startDraft,
            startEdit: setEditingMaatregel,
            save: onSaveMaatregel,
            copyToNieuweVersie,
            isCopiedToNieuweVersie,
            cancel: cancelCreateOrEditMaatregel,
            deleteMaatregel,
            acceptToelichting,
            removeToelichting,
            maatregelen: filteredMaatregelen,
            draftMaatregel,
            isEditEnabled: draftMaatregel === null && editingMaatregel === null,
            isSectionEmpty: draftMaatregel === null && filteredMaatregelen.length === 0,
        }),
        [
            startDraft,
            setEditingMaatregel,
            onSaveMaatregel,
            copyToNieuweVersie,
            isCopiedToNieuweVersie,
            cancelCreateOrEditMaatregel,
            deleteMaatregel,
            acceptToelichting,
            removeToelichting,
            filteredMaatregelen,
            draftMaatregel,
            editingMaatregel,
        ]
    );
}
